/**
 * th-url-decorator.js v2 — campaign attribution capture + telehealth link decoration.
 *
 * MANAGED IN THE IR_2.0 REPOSITORY. Do not edit in the Shopify admin code editor;
 * admin edits bypass review and are overwritten by the next repo deploy.
 * Spec: docs/th-url-decorator-v2-spec.md (+ docs/th-url-decorator-v2-simplified-review.md).
 *
 * What it does:
 *  1. Capture: on a landing from OUTSIDE the site whose URL carries allowlisted
 *     campaign params, store them in the `ir_attr_lt` cookie on .intimaterose.com
 *     (30 days, consent-gated in opt-in regions).
 *  2. Decorate: when a click targets an approved telehealth host, append the
 *     campaign params (current URL first, stored cookie as fallback) plus the
 *     edgetag/fb identity params to the destination URL. Existing destination
 *     params are never modified — appends only, original bytes preserved.
 *
 * Safety invariants (see spec §9): never preventDefault/stopPropagation, the only
 * DOM mutation is the matched anchor's href, no network calls/timers/observers,
 * every entry point try/caught, all failures degrade to plain navigation.
 *
 * Rollout flags below: Stage 1 shipped CAPTURE_ENABLED=true, AUTO_DECORATE_ENABLED=false
 * (decoration required data-decorate-url="true", which covered only 18 of 238 telehealth
 * links). Stage 2 sets AUTO_DECORATE_ENABLED=true: every link to a DECORATE_HOSTS host is
 * decorated regardless of attribute; data-decorate-url="false" on the link or an ancestor
 * still opts out.
 */
(function () {
  'use strict';

  try {
    if (window.__irThDecoratorV2) return; // duplicate include guard
    window.__irThDecoratorV2 = true;

    if (typeof URL !== 'function' || typeof URLSearchParams !== 'function' ||
        !document || !document.addEventListener) return;

    var config = {
      CAPTURE_ENABLED: true,
      AUTO_DECORATE_ENABLED: true, // Stage 2: every allowlisted-host link decorates; data-decorate-url="false" opts out

      // Decoration allowlist. Deliberate EXCLUSIONS (not omissions) — spec §host inventory:
      //   www./apex intimaterose.com  — the storefront itself (decorating internal links = pollution)
      //   pt.intimaterose.com        — OpenLoop PT; carries email= PII params; pending owner sign-off
      //   health./staging-health.    — OpenLoop patient API bases, not navigable destinations
      //   tbzku.intimaterose.com     — EdgeTag event collector
      //   load.tracking.…            — first-party GTM script host, never a link target
      //   intimate-rose.myshopify.com — canonical Shopify domain (internal)
      DECORATE_HOSTS: ['secure.intimaterose.com', 'hrt.intimaterose.com'],

      // Priority-ordered capture/forward allowlist. `glid` (v1) removed — unconfirmed
      // typo of gclid; re-add only with telehealth-owner confirmation (spec Q5).
      PARAM_KEYS: [
        'utm_source', 'utm_medium', 'utm_campaign', 'gclid', 'fbclid',
        'utm_id', 'utm_term', 'utm_content', 'ref', 'attr_signup_ref_code_text'
      ],

      MAX_VALUE_LEN: 200,
      MAX_COOKIE_BYTES: 1200,
      MAX_URL_LEN: 4096,
      COOKIE_NAME: 'ir_attr_lt',
      COOKIE_MAX_AGE: 2592000, // 30 days
      CONSENT_KEY: 'ir_consent', // written by the theme.liquid consent bootstrap
      CONSENT_TTL_MS: 180 * 24 * 3600 * 1000
    };

    var INTERNAL_APEX = 'intimaterose.com';
    var INTERNAL_EXTRA = 'intimate-rose.myshopify.com';

    // ---------- debug (silent in production) ----------

    function debugOn() {
      try {
        if (window.location.search.indexOf('th_debug=1') !== -1) return true;
        return window.localStorage && window.localStorage.getItem('thDebug') === '1';
      } catch (e) { return false; }
    }
    var DEBUG = debugOn();
    function log() {
      if (!DEBUG) return;
      try { console.log.apply(console, ['[TH v2]'].concat([].slice.call(arguments))); } catch (e) {}
    }

    // ---------- pure helpers ----------

    function truncate(value) {
      value = String(value);
      return value.length > config.MAX_VALUE_LEN ? value.slice(0, config.MAX_VALUE_LEN) : value;
    }

    function parseAllowedParams(search) {
      var out = {};
      try {
        var params = new URLSearchParams(search || '');
        for (var i = 0; i < config.PARAM_KEYS.length; i++) {
          var key = config.PARAM_KEYS[i];
          var value = params.get(key);
          if (value) out[key] = truncate(value);
        }
      } catch (e) { /* malformed query — capture nothing */ }
      return out;
    }

    function isInternalHost(hostname) {
      hostname = String(hostname || '').toLowerCase();
      return hostname === INTERNAL_APEX ||
             hostname.slice(-(INTERNAL_APEX.length + 1)) === '.' + INTERNAL_APEX ||
             hostname === INTERNAL_EXTRA;
    }

    function shouldCapture(search, referrer) {
      var params = parseAllowedParams(search);
      var hasParams = false;
      for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) { hasParams = true; break; } }
      if (!hasParams) return false;
      if (!referrer) return true;
      try {
        return !isInternalHost(new URL(referrer).hostname);
      } catch (e) { return true; } // unparseable referrer — treat as external landing
    }

    function buildCookieValue(params, ts, lp) {
      var payload = { v: 1, ts: ts, lp: truncate(lp || '') };
      var present = [];
      for (var i = 0; i < config.PARAM_KEYS.length; i++) {
        var key = config.PARAM_KEYS[i];
        if (params[key]) { payload[key] = truncate(params[key]); present.push(key); }
      }
      var value = encodeURIComponent(JSON.stringify(payload));
      // Over budget: drop lowest-priority keys first, then the landing path.
      while (value.length > config.MAX_COOKIE_BYTES && (present.length || payload.lp)) {
        if (present.length) delete payload[present.pop()];
        else payload.lp = '';
        value = encodeURIComponent(JSON.stringify(payload));
      }
      return value;
    }

    function parseStored(raw) {
      if (!raw) return null;
      try {
        var parsed = JSON.parse(decodeURIComponent(raw));
        return parsed && parsed.v === 1 ? parsed : null;
      } catch (e) { return null; }
    }

    function hostAllowed(hostname) {
      return config.DECORATE_HOSTS.indexOf(String(hostname || '').toLowerCase()) !== -1;
    }

    // Append-only URL builder: original bytes (including existing params and their
    // exact encoding) are never touched; params land before any #fragment; final
    // length is capped — a param that would exceed the cap is skipped, not truncated.
    function decorate(href, pageParams, stored, ids) {
      var parsed = new URL(href); // throws on malformed → caller catches
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return href;
      var existing = parsed.searchParams;

      var hashIndex = href.indexOf('#');
      var base = hashIndex === -1 ? href : href.slice(0, hashIndex);
      var fragment = hashIndex === -1 ? '' : href.slice(hashIndex);

      function appendIfRoom(key, value) {
        if (!value) return;
        if (existing.has(key)) return; // destination always wins
        var piece = (base.indexOf('?') === -1 ? '?'
          : (base.slice(-1) === '?' || base.slice(-1) === '&') ? '' : '&')
          + encodeURIComponent(key) + '=' + encodeURIComponent(truncate(value));
        if (base.length + piece.length + fragment.length > config.MAX_URL_LEN) return;
        base += piece;
        existing.set(key, 'x'); // mark as present so duplicates are not appended
      }

      for (var i = 0; i < config.PARAM_KEYS.length; i++) {
        var key = config.PARAM_KEYS[i];
        appendIfRoom(key, pageParams[key] || (stored && stored[key]));
      }
      appendIfRoom('edgetag_id', ids.edgetag_id);
      appendIfRoom('fbp', ids.fbp);
      appendIfRoom('fbc', ids.fbc);

      return base + fragment;
    }

    // Overridable seam for tests; production behavior is identical.
    var hooks = { decorate: decorate };

    // ---------- environment access ----------

    function getCookie(name) {
      try {
        var match = document.cookie.match(
          new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)')
        );
        return match ? match[1] : null;
      } catch (e) { return null; }
    }

    // Consent has three states, not two. On a FIRST-EVER pageview the theme's
    // consent bootstrap has no cached region, so irConsentBypass is false and
    // consent-gate-eu-us.js resolves geo asynchronously — 'unknown' here. The
    // gate persists ir_consent and dispatches `irConsentReady` once resolved,
    // which is when a deferred capture completes (see pendingCapture below).
    function consentState() {
      if (window.irConsentBypass === true) return 'granted';
      try {
        var raw = window.localStorage && window.localStorage.getItem(config.CONSENT_KEY);
        if (!raw) return 'unknown';
        var parsed = JSON.parse(raw);
        if (!parsed) return 'unknown';
        if (parsed.ts && Date.now() - parsed.ts > config.CONSENT_TTL_MS) return 'unknown';
        return parsed.m ? 'granted' : 'declined';
      } catch (e) { return 'unknown'; }
    }

    function writeAttributionCookie(value) {
      var attrs = '; path=/; max-age=' + config.COOKIE_MAX_AGE + '; SameSite=Lax';
      var hostname = String(window.location.hostname || '').toLowerCase();
      if (hostname === INTERNAL_APEX || hostname.slice(-(INTERNAL_APEX.length + 1)) === '.' + INTERNAL_APEX) {
        attrs += '; domain=.' + INTERNAL_APEX;
      }
      if (window.location.protocol === 'https:') attrs += '; Secure';
      document.cookie = config.COOKIE_NAME + '=' + value + attrs;
    }

    function collectIds() {
      var ids = {};
      var tag = getCookie('tag_user_id');
      var fbp = getCookie('_fbp');
      var fbc = getCookie('_fbc');
      if (tag) ids.edgetag_id = truncate(decodeSafe(tag));
      if (fbp) ids.fbp = truncate(decodeSafe(fbp));
      if (fbc) ids.fbc = truncate(decodeSafe(fbc));
      return ids;
    }

    function decodeSafe(value) {
      try { return decodeURIComponent(value); } catch (e) { return value; }
    }

    function pushError(err, ctaId) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'th_link_error',
          error_message: err && err.message ? err.message : 'unknown',
          cta_id: ctaId || 'unknown',
          page_location: window.location.href
        });
      } catch (e) { /* never escalate */ }
    }

    // ---------- capture (page load) ----------

    var pendingCapture = null; // landing params held while consent is undetermined

    function performCapture(captured) {
      var value = buildCookieValue(captured.params, captured.ts, captured.lp);
      writeAttributionCookie(value); // whole-touch replacement of any prior value
      log('captured', captured.params);
    }

    function flushPendingCapture() {
      if (!pendingCapture) return;
      var state = consentState();
      if (state === 'granted') { var held = pendingCapture; pendingCapture = null; performCapture(held); }
      else if (state === 'declined') pendingCapture = null;
      // 'unknown' → keep holding; retried on irConsentReady and on clicks
    }

    try {
      if (config.CAPTURE_ENABLED &&
          shouldCapture(window.location.search, document.referrer)) {
        var captured = {
          params: parseAllowedParams(window.location.search),
          ts: new Date().toISOString(),
          lp: window.location.pathname
        };
        var state = consentState();
        if (state === 'granted') performCapture(captured);
        else if (state === 'unknown') { pendingCapture = captured; log('capture pending consent'); }
      }
    } catch (e) { log('capture failed', e); }

    document.addEventListener('irConsentReady', function () {
      try { flushPendingCapture(); } catch (e) { log('flush failed', e); }
    }, true);

    // ---------- decoration (click) ----------

    function handleClick(event) {
      var link = null;
      try { flushPendingCapture(); } catch (e) { /* capture must never affect clicks */ }
      try {
        if (!event || !event.target || typeof event.target.closest !== 'function') return;
        link = event.target.closest('a[href]');
        if (!link) return;
        if (link.closest('[data-decorate-url="false"]')) return;

        var parsed = new URL(link.href);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
        if (!hostAllowed(parsed.hostname)) return;
        if (!config.AUTO_DECORATE_ENABLED && !link.closest('[data-decorate-url="true"]')) return;

        var decorated = hooks.decorate(
          link.href,
          parseAllowedParams(window.location.search),
          parseStored(getCookie(config.COOKIE_NAME)) || {},
          collectIds()
        );
        if (decorated && decorated !== link.href) {
          link.href = decorated;
          log('decorated', decorated);
        }
      } catch (err) {
        // Original href untouched; navigation proceeds normally.
        pushError(err, link && link.getAttribute && link.getAttribute('data-cta-id'));
        log('decoration error', err);
      }
    }

    document.addEventListener('click', handleClick, true);

    // ---------- public API ----------

    window.IntimateRoseTracking = window.IntimateRoseTracking || {};
    window.IntimateRoseTracking.buildDecoratedUrl = function (href) {
      try {
        if (!hostAllowed(new URL(href).hostname)) return href;
        return hooks.decorate(
          href,
          parseAllowedParams(window.location.search),
          parseStored(getCookie(config.COOKIE_NAME)) || {},
          collectIds()
        );
      } catch (e) { return href; }
    };
    window.IntimateRoseTracking.getStoredAttribution = function () {
      var lt = parseStored(getCookie(config.COOKIE_NAME));
      return lt ? { lt: lt } : null;
    };

    if (window.__TH_TEST__ === true) {
      window.IntimateRoseTracking._internals = {
        config: config,
        hooks: hooks,
        parseAllowedParams: parseAllowedParams,
        shouldCapture: shouldCapture,
        buildCookieValue: buildCookieValue,
        parseStored: parseStored,
        hostAllowed: hostAllowed,
        decorate: decorate
      };
    }

    log('loaded', { capture: config.CAPTURE_ENABLED, auto: config.AUTO_DECORATE_ENABLED });
  } catch (e) {
    /* Entire script is best-effort: any init failure leaves the page untouched. */
  }
})();
